import React, { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/helpers';
import { difficultyConfig, priorityConfig } from './constants';
import type { DifficultyLevel, PriorityLevel } from './types';

// ============================================================================
// Priority Icon - Segmented ring with "!" in center (matching planner style)
// ============================================================================
export const PriorityIcon = ({ level, activeColor: _activeColor, size = 24, colorOverride }: { level: 1 | 2 | 3 | 4; activeColor: string; size?: number; colorOverride?: string }) => {
  // Color based on priority level: Low = green, Medium = orange, High = orange-red, Urgent = red
  const colors: Record<number, string> = {
    1: '#22c55e',  // green for low
    2: '#f97316',  // orange for medium
    3: '#ea580c',  // orange-red for high
    4: '#ef4444',  // red for urgent
  };
  const color = colorOverride || colors[level] || '#9ca3af';

  const cx = size / 2, cy = size / 2;
  const oR = size * 0.42, iR = size * 0.24;
  const segs = 12, gap = 0.08;

  const seg = (i: number) => {
    const a = (Math.PI * 2) / segs;
    const st = i * a - Math.PI / 2, en = st + a - gap;
    const o1 = { x: cx + Math.cos(st) * oR, y: cy + Math.sin(st) * oR };
    const o2 = { x: cx + Math.cos(en) * oR, y: cy + Math.sin(en) * oR };
    const i1 = { x: cx + Math.cos(en) * iR, y: cy + Math.sin(en) * iR };
    const i2 = { x: cx + Math.cos(st) * iR, y: cy + Math.sin(st) * iR };
    return `M ${o1.x} ${o1.y} A ${oR} ${oR} 0 0 1 ${o2.x} ${o2.y} L ${i1.x} ${i1.y} A ${iR} ${iR} 0 0 0 ${i2.x} ${i2.y} Z`;
  };

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
      {[...Array(segs)].map((_, i) => (
        <path key={i} d={seg(i)} fill={color} />
      ))}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill={color} fontSize={size * 0.38} fontWeight="700">!</text>
    </svg>
  );
};

// ============================================================================
// Difficulty Icon - Bar chart style (matching planner style)
// ============================================================================
export const DifficultyIcon = memo(({ difficulty, size = 16 }: { difficulty?: 'easy' | 'medium' | 'hard'; size?: number }) => {
  const colors: Record<string, string> = { easy: '#22c55e', medium: '#eab308', hard: '#ef4444' };
  const color = difficulty ? colors[difficulty] : 'var(--icon-muted)';

  const w = size * 0.18, gap = size * 0.08;
  const heights = [size * 0.28, size * 0.48, size * 0.72];
  const totalW = w * 3 + gap * 2;
  const startX = (size - totalW) / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      {[0, 1, 2].map(i => (
        <rect
          key={i}
          x={startX + i * (w + gap)}
          y={size * 0.88 - heights[i]}
          width={w}
          height={heights[i]}
          rx={w / 2}
          fill={color}
        />
      ))}
    </svg>
  );
});

// ============================================================================
// Difficulty Selector Component
// ============================================================================
interface DifficultySelectorProps {
  value?: DifficultyLevel;
  onChange: (difficulty: DifficultyLevel | undefined) => void;
  size?: 'sm' | 'md';
  variant?: 'badge' | 'full';
}

export function DifficultySelector({
  value,
  onChange,
  size = 'md',
  variant = 'badge',
}: DifficultySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const hasValue = value !== undefined && value !== null;
  const currentDifficulty = value || 'medium';
  const config = difficultyConfig[currentDifficulty];

  const sizeClasses = size === 'sm'
    ? variant === 'badge' ? 'h-6 w-[72px] px-1.5 text-[9px] justify-center' : 'h-8 px-2.5 gap-1.5 text-sm'
    : variant === 'badge' ? 'h-7 w-[80px] px-2 text-[10px] justify-center' : 'h-9 px-3 gap-2 text-sm';

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 120;
      const viewportWidth = window.innerWidth;
      const padding = 8;

      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - padding) {
        left = Math.max(padding, rect.right - dropdownWidth);
      }

      setDropdownPos({
        top: rect.bottom + 4,
        left: left,
      });
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden py-1"
      style={{ top: dropdownPos.top, left: dropdownPos.left, width: 100 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* None option first */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onChange(undefined);
          setIsOpen(false);
        }}
        className={cn(
          'w-full px-2.5 py-1.5 text-[11px] flex items-center gap-2 font-medium transition-colors',
          !hasValue ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        )}
      >
        <DifficultyIcon size={14} />
        <span className="text-gray-700 dark:text-gray-300">None</span>
      </button>
      {/* Difficulty options: Easy, Medium, Hard */}
      {(['easy', 'medium', 'hard'] as const).map((diff) => {
        const isSelected = value === diff;
        return (
          <button
            key={diff}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onChange(diff);
              setIsOpen(false);
            }}
            className={cn(
              'w-full px-2.5 py-1.5 text-[11px] flex items-center gap-2 font-medium transition-colors',
              isSelected ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            )}
          >
            <DifficultyIcon difficulty={diff} size={14} />
            <span className="text-gray-700 dark:text-gray-300">{difficultyConfig[diff].label}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  // Show "Add" button when no value
  if (!hasValue) {
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleOpen}
          className={cn(
            'rounded-lg font-medium shrink-0 transition-colors cursor-pointer flex items-center justify-center',
            'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
            'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700',
            sizeClasses
          )}
        >
          <DifficultyIcon size={14} />
        </button>
        {dropdownContent && createPortal(dropdownContent, document.body)}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={cn(
          'rounded-lg font-medium shrink-0 transition-colors cursor-pointer flex items-center',
          variant === 'badge' && 'uppercase tracking-wide',
          sizeClasses,
          config.bg,
          config.text,
          config.hover
        )}
      >
        {variant === 'full' && <DifficultyIcon difficulty={currentDifficulty} size={16} />}
        {config.label}
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}

// ============================================================================
// Priority Selector Component
// ============================================================================
interface PrioritySelectorProps {
  value?: string;
  onChange: (priority: PriorityLevel | undefined) => void;
  size?: 'xs' | 'sm' | 'md';
  variant?: 'icon' | 'full';
}

export function PrioritySelector({
  value,
  onChange,
  size = 'md',
  variant = 'icon',
}: PrioritySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const hasValue = value !== undefined && value !== null && value !== '';
  const currentPriority = (value as PriorityLevel) || 'medium';
  const config = priorityConfig[currentPriority] || priorityConfig.medium;

  const sizeClasses = size === 'xs'
    ? variant === 'icon' ? 'h-5 w-5' : 'h-6 px-1.5 gap-1 text-xs'
    : size === 'sm'
    ? variant === 'icon' ? 'h-6 w-6' : 'h-8 px-2.5 gap-1.5 text-sm'
    : variant === 'icon' ? 'h-7 w-7' : 'h-9 px-3 gap-2 text-sm';

  const iconSizeClasses = size === 'xs'
    ? 'w-3 h-3'
    : size === 'sm'
    ? 'w-4 h-4'
    : 'w-5 h-5';

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 120;
      const viewportWidth = window.innerWidth;
      const padding = 8;

      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - padding) {
        left = Math.max(padding, rect.right - dropdownWidth);
      }

      setDropdownPos({
        top: rect.bottom + 4,
        left: left,
      });
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden py-1"
      style={{ top: dropdownPos.top, left: dropdownPos.left, width: 100 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* None option first */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onChange(undefined);
          setIsOpen(false);
        }}
        className={cn(
          'w-full px-2.5 py-1.5 text-[11px] flex items-center gap-2 font-medium transition-colors',
          !hasValue ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        )}
      >
        <div className="w-4 h-4">
          <PriorityIcon level={1} activeColor="#9ca3af" size={16} colorOverride="#d1d5db" />
        </div>
        <span className="text-gray-700 dark:text-gray-300">None</span>
      </button>
      {/* Priority options: High, Medium, Low (matching planner) */}
      {(['high', 'medium', 'low'] as const).map((prio) => {
        const prioConfig = priorityConfig[prio];
        const isSelected = value === prio;
        return (
          <button
            key={prio}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onChange(prio);
              setIsOpen(false);
            }}
            className={cn(
              'w-full px-2.5 py-1.5 text-[11px] flex items-center gap-2 font-medium transition-colors',
              isSelected ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            )}
          >
            <div className="w-4 h-4">
              <PriorityIcon level={prioConfig.level} activeColor={prioConfig.activeColor} size={16} />
            </div>
            <span className="text-gray-700 dark:text-gray-300">{prioConfig.label}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  // Show "Add" button when no value - gray color
  if (!hasValue) {
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleOpen}
          title="Add priority"
          className={cn(
            'rounded-lg flex items-center justify-center shrink-0 transition-colors cursor-pointer',
            'text-gray-300 hover:text-gray-400 dark:text-gray-600 dark:hover:text-gray-500',
            sizeClasses
          )}
        >
          <div className={iconSizeClasses}>
            <PriorityIcon level={1} activeColor="#9ca3af" colorOverride="#d1d5db" />
          </div>
        </button>
        {dropdownContent && createPortal(dropdownContent, document.body)}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        title={config.label}
        className={cn(
          'rounded-lg flex items-center justify-center shrink-0 transition-colors cursor-pointer',
          sizeClasses,
          config.bg,
          config.hover
        )}
      >
        <div className={iconSizeClasses}>
          <PriorityIcon level={config.level} activeColor={config.activeColor} />
        </div>
        {variant === 'full' && (
          <span className={cn('font-medium', config.text)}>{config.label}</span>
        )}
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}
